import { type ReactNode, useCallback } from 'react';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import type { FileListItem } from '@/features/section-attribute/types';
import { downloadFile, downloadFileDirectly } from '@/lib/api/utils';
import { cn } from '@/lib/cn';
import { getBaseUrl } from '@/lib/env';
import { getFileIcon } from '@/utils/file/fileIconMapper';
import { CFormItemBase, type CFormItemProps, buildCFormItem } from '../CFormItem';
type Props = CFormItemProps & {
  /** 表示するファイル一覧（表示用の一部） */
  files: FileListItem[];
  /** フォルダ配下の総ファイル数（「+N more」表示に使用） */
  totalCount: number;
  /** 最大表示件数（デフォルト: 5） */
  maxDisplay?: number;
  /** 「View All」クリック時のハンドラ */
  onViewAll?: () => void;
  /** 編集モード時のファイル変更ハンドラ */
  onChange?: (files: FileListItem[]) => void;
  /** 読取時のカスタムコンポーネント */
  readComponent?: ReactNode;
  /** 編集時のカスタムコンポーネント（ファイルアップロードUIなど） */
  writeComponent?: ReactNode;
};
const DEFAULT_MAX_DISPLAY = 5;

/**
 * Card-based Form / FileListItem
 *
 * ファイル一覧表示コンポーネント
 * 複数ファイルのサムネイル表示に使用
 * フォルダ詳細画面の Child Files 表示を想定
 * writeComponent を指定することで編集モードに対応
 */
export const CFormFileListItem = buildCFormItem<Props>((props: Props) => {
  const maxDisplay = props.maxDisplay ?? DEFAULT_MAX_DISPLAY;
  const displayFiles = props.files.slice(0, maxDisplay);
  const remainingCount = Math.max(0, props.totalCount - displayFiles.length);
  const defaultReadComponent = props.files.length > 0 ? <VStack className="gap-2">
        {displayFiles.map(file => <FileItem key={file.id} file={file} />)}
        {remainingCount > 0 && <HStack className="items-center gap-2">
            {props.onViewAll ? <button type="button" onClick={props.onViewAll} className="text-link hover:text-link-hover hover:underline">
                <Text variant="pSmall">+{remainingCount} more</Text>
              </button> : <Text variant="pSmall" className="text-neutral-muted-on-fill">
                +{remainingCount} more
              </Text>}
          </HStack>}
      </VStack> : <Text variant="pSmall" className="text-neutral-muted-on-fill">
        -
      </Text>;
  return <CFormItemBase {...props} readComponent={props.readComponent ?? defaultReadComponent} writeComponent={props.writeComponent} />;
});

/**
 * 個別ファイルアイテムの表示コンポーネント
 */
function FileItem({
  file
}: {
  file: FileListItem;
}) {
  const isImage = file.mimeType.startsWith('image/') && file.thumbnailUrl != null && file.thumbnailUrl.length > 0;
  const handleDownload = useCallback(() => {
    if (!file.downloadApiPath) return;
    const path = file.downloadApiPath;
    const isAbsoluteUrl = path.startsWith('http://') || path.startsWith('https://');
    if (isAbsoluteUrl) {
      downloadFileDirectly(path, file.name);
    } else {
      const fullUrl = `${getBaseUrl()}${path}`;
      downloadFile(fullUrl, file.name);
    }
  }, [file.downloadApiPath, file.name]);
  return <HStack className="items-center gap-2">
      <div className={cn('flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-primary-muted-fill')}>
        {isImage && file.thumbnailUrl ? <img src={file.thumbnailUrl} alt={file.name} className="size-full object-cover" loading="lazy" decoding="async" /> : <Icon iconName={getFileIcon(file.mimeType)} className="size-4 text-neutral-muted-on-fill" />}
      </div>
      <Text variant="pSmall" className="min-w-0 flex-1 truncate">
        {file.name}
      </Text>
      {file.downloadApiPath && <Button variant="ghost" size="sm" className="shrink-0" onClick={handleDownload}>
          <Icon iconName="Download" className="size-4" />
        </Button>}
    </HStack>;
}